import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Radar, RefreshCw, Truck, MapPin, Package, Clock, Phone, WifiOff, History, Zap, PauseCircle, AlertTriangle } from "lucide-react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import RastroHistorico from "./RastroHistorico";
import { AcordarGpsButton } from "./monitoramento/AcordarGpsButton";

// Monitoramento em Tempo Real — mapa com posições GPS dos entregadores ativos
// (fev/2026). Polling 10s. Backend endpoint: GET /tracking/live.
//
// Iter 81 (fev/2026): Bloco 1 (movimentos suaves) + Bloco 3 (painel de status)
// - Ícone rotaciona pelo heading do GPS (ou bearing calculado do trail)
// - Trail com cor única por entregador (hash do id → 8 cores)
// - 4 estados: moving/stopped/no_signal/offline com cores verde/amarelo/vermelho/cinza
// - ETA proxy baseada em velocidade atual (2km @ speed → "chega em ~X min")
// - Alerta automático via toast quando alguém entra em no_signal

const initials = (nome) =>
  (nome || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");

// 8 cores fixas — bem separadas visualmente no mapa
const DRIVER_COLORS = [
  "#8b5cf6", "#ec4899", "#0ea5e9", "#f59e0b",
  "#10b981", "#ef4444", "#6366f1", "#14b8a6",
];

// Hash simples e determinístico do entregador_id → cor.
// Mesmo entregador sempre pega a mesma cor entre reloads.
function colorForDriver(eid) {
  let h = 0;
  for (const ch of String(eid || "")) h = (h * 31 + ch.charCodeAt(0)) | 0;
  return DRIVER_COLORS[Math.abs(h) % DRIVER_COLORS.length];
}

// Status computado no cliente — combina posição + heartbeat + speed.
// - moving   (verde): position fresh (<3min) + speed > 0.5 m/s
// - stopped  (amarelo): position fresh mas parado há mais de 2min
// - no_signal (vermelho): posição antiga (>3min) OU heartbeat stale
// - offline  (cinza): sem posição
function computeDriverStatus(e) {
  if (!e.position) return "offline";
  const ma = e.position.minutes_ago;
  if (e.stale || e.heartbeat_stale || (ma !== null && ma !== undefined && ma > 3)) return "no_signal";
  const speed = Number(e.position.speed || 0);
  if (speed > 0.5) return "moving";
  // Parado — se última atualização há >2min, marca amarelo (mudou pouco)
  if (ma !== null && ma !== undefined && ma >= 2) return "stopped";
  return "moving";  // recém-atualizado mas parado — ainda "ok"
}

const STATUS_META = {
  moving:    { color: "#10b981", label: "Em movimento",   icon: Zap },
  stopped:   { color: "#f59e0b", label: "Parado",          icon: PauseCircle },
  no_signal: { color: "#ef4444", label: "Sem sinal",       icon: AlertTriangle },
  offline:   { color: "#94a3b8", label: "Offline",         icon: WifiOff },
};

// ETA proxy — sem coord de destino, estimamos "no ritmo atual chega em Y min"
// baseado num raio médio de 2km entre entregas urbanas. Retorna null se
// não faz sentido calcular (parado ou sem speed confiável).
function estimateETA(speedMps) {
  const s = Number(speedMps || 0);
  if (s < 1.5) return null; // <5.4 km/h — ETA sem sentido
  const AVG_DISTANCE_M = 2000;
  const minutes = Math.round((AVG_DISTANCE_M / s) / 60);
  if (minutes < 1 || minutes > 30) return null;
  return minutes;
}

// Bearing entre 2 pontos GPS — usado pra rotacionar o ícone quando o
// backend não manda heading (fallback).
function bearingBetween(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const toDeg = (r) => (r * 180) / Math.PI;
  const φ1 = toRad(a[0]), φ2 = toRad(b[0]);
  const Δλ = toRad(b[1] - a[1]);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

// Ícone customizado — pino colorido pelo status + seta apontando pra direção
// do movimento quando `moving`. Cinza no `offline`, vermelho no `no_signal`.
function driverIcon(nome, count, status, heading) {
  const meta = STATUS_META[status] || STATUS_META.offline;
  const showArrow = status === "moving" && Number.isFinite(heading);
  const rotate = showArrow ? `transform: rotate(${heading}deg); transform-origin: 22px 22px;` : "";
  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative; width:44px; height:44px;">
        ${showArrow ? `
          <div style="position:absolute; inset:0; ${rotate}; pointer-events:none;">
            <div style="position:absolute; top:-8px; left:50%; margin-left:-6px;
                        width:0; height:0;
                        border-left:6px solid transparent;
                        border-right:6px solid transparent;
                        border-bottom:10px solid ${meta.color};
                        filter: drop-shadow(0 1px 2px rgba(0,0,0,.3));"></div>
          </div>
        ` : ""}
        <div style="background:${meta.color};color:#fff;border:3px solid #fff;border-radius:9999px;width:44px;height:44px;box-shadow:0 6px 12px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;">
          ${initials(nome)}
        </div>
        ${status === "moving" ? `
          <div style="position:absolute; inset:0; border-radius:9999px;
                      border: 2px solid ${meta.color}; opacity: .5;
                      animation: pulseRing 1.8s ease-out infinite;"></div>
        ` : ""}
        <div style="position:absolute;top:-6px;right:-8px;background:#ea580c;color:#fff;border-radius:9999px;min-width:22px;height:22px;padding:0 4px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;border:2px solid #fff;">${count}</div>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
}

function LiveMap({ entregadores, focusEid, onSelectDriver }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersRef = useRef({}); // eid → marker
  const trailsRef = useRef({});  // eid → { polyline, points: [[lat,lng], ...] }
  const animatingRef = useRef({}); // eid → { rafId }
  const headingRef = useRef({});   // eid → last bearing (pra evitar flick quando parado)
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true }).setView(
      [-16.3287, -48.9530], 12
    );
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => {
      Object.values(animatingRef.current).forEach((a) => a?.rafId && cancelAnimationFrame(a.rafId));
      animatingRef.current = {};
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
      trailsRef.current = {};
      headingRef.current = {};
      fittedRef.current = false;
    };
  }, []);

  const animateMarker = (eid, fromLatLng, toLatLng, distanceM) => {
    const marker = markersRef.current[eid];
    if (!marker) return;
    if (animatingRef.current[eid]?.rafId) {
      cancelAnimationFrame(animatingRef.current[eid].rafId);
    }
    const ANIM_SPEED_MPS = 6;
    const desired = ((distanceM || 1) / ANIM_SPEED_MPS) * 1000;
    const duration = Math.max(400, Math.min(3000, desired));
    const start = performance.now();
    const ease = (t) => 1 - (1 - t) * (1 - t);
    const step = (t) => {
      const raw = Math.min(1, (t - start) / duration);
      const progress = ease(raw);
      const lat = fromLatLng[0] + (toLatLng[0] - fromLatLng[0]) * progress;
      const lng = fromLatLng[1] + (toLatLng[1] - fromLatLng[1]) * progress;
      marker.setLatLng([lat, lng]);
      if (raw < 1) {
        animatingRef.current[eid] = { rafId: requestAnimationFrame(step) };
      } else {
        animatingRef.current[eid] = null;
      }
    };
    animatingRef.current[eid] = { rafId: requestAnimationFrame(step) };
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const MAX_TRAIL_POINTS = 20;
    const activeIds = new Set(
      entregadores.filter((e) => e.position).map((e) => e.entregador_id)
    );
    Object.keys(markersRef.current).forEach((eid) => {
      if (!activeIds.has(eid)) {
        map.removeLayer(markersRef.current[eid]);
        delete markersRef.current[eid];
      }
    });
    Object.keys(trailsRef.current).forEach((eid) => {
      if (!activeIds.has(eid)) {
        const t = trailsRef.current[eid];
        if (t?.polyline) map.removeLayer(t.polyline);
        delete trailsRef.current[eid];
      }
    });
    entregadores.forEach((e) => {
      if (!e.position) return;
      const { latitude, longitude } = e.position;
      const status = computeDriverStatus(e);
      // Direção: prefere heading do GPS, fallback pra bearing calculado
      let heading = Number(e.position.heading);
      if (!Number.isFinite(heading)) {
        const trail = trailsRef.current[e.entregador_id];
        if (trail && trail.points.length >= 1) {
          const prev = trail.points[trail.points.length - 1];
          if (prev[0] !== latitude || prev[1] !== longitude) {
            heading = bearingBetween(prev, [latitude, longitude]);
          }
        }
      }
      // Persiste último heading conhecido pra não zerar quando GPS pausa
      if (Number.isFinite(heading)) headingRef.current[e.entregador_id] = heading;
      const finalHeading = headingRef.current[e.entregador_id];

      const icon = driverIcon(e.nome, e.entregas_count, status, finalHeading);
      if (markersRef.current[e.entregador_id]) {
        const prev = markersRef.current[e.entregador_id].getLatLng();
        const dist = map.distance([prev.lat, prev.lng], [latitude, longitude]);
        if (dist > 0.5 && dist < 5000) {
          animateMarker(e.entregador_id, [prev.lat, prev.lng], [latitude, longitude], dist);
        } else {
          markersRef.current[e.entregador_id].setLatLng([latitude, longitude]);
        }
        markersRef.current[e.entregador_id].setIcon(icon);
      } else {
        const marker = L.marker([latitude, longitude], { icon }).addTo(map);
        marker.on("click", () => onSelectDriver?.(e.entregador_id));
        markersRef.current[e.entregador_id] = marker;
      }
      let trail = trailsRef.current[e.entregador_id];
      if (!trail) {
        trail = { points: [], polyline: null };
        trailsRef.current[e.entregador_id] = trail;
      }
      const last = trail.points[trail.points.length - 1];
      if (!last || last[0] !== latitude || last[1] !== longitude) {
        trail.points.push([latitude, longitude]);
        if (trail.points.length > MAX_TRAIL_POINTS) trail.points.shift();
      }
      if (trail.polyline) map.removeLayer(trail.polyline);
      if (trail.points.length >= 2) {
        trail.polyline = L.polyline(trail.points, {
          color: colorForDriver(e.entregador_id),  // cor única por entregador
          weight: 3,
          opacity: 0.6,
          lineCap: "round",
          lineJoin: "round",
        }).addTo(map);
      }
    });
    if (!fittedRef.current) {
      const pts = entregadores
        .filter((e) => e.position)
        .map((e) => [e.position.latitude, e.position.longitude]);
      if (pts.length > 0) {
        fittedRef.current = true;
        if (pts.length === 1) map.setView(pts[0], 15);
        else map.fitBounds(L.latLngBounds(pts), { padding: [40, 40] });
      }
    }
  }, [entregadores, onSelectDriver]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusEid) return;
    const ent = entregadores.find((e) => e.entregador_id === focusEid);
    if (ent?.position) {
      map.flyTo([ent.position.latitude, ent.position.longitude], 16, {
        duration: 0.8,
      });
    }
  }, [focusEid, entregadores]);

  return (
    <div
      ref={containerRef}
      data-testid="monitoramento-map"
      style={{
        height: "72vh",
        minHeight: 480,
        width: "100%",
        borderRadius: 12,
        overflow: "hidden",
      }}
    />
  );
}

export default function MonitoramentoVivo() {
  const { currentEmpresaId } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedEid, setSelectedEid] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  // Iter 81 (fev/2026): rastreia quem já entrou em `no_signal` pra evitar spam
  // de toast — só dispara quando o entregador ENTRA nesse estado.
  const prevNoSignalRef = useRef(new Set());
  // Iter 82 (fev/2026): backpressure — evita 2 requests simultâneas se uma
  // travar. Também controla toast de erro com cooldown de 30s.
  const inFlightRef = useRef(false);
  const lastErrorToastRef = useRef(0);

  const load = async () => {
    if (inFlightRef.current) return;  // evita sobreposição no polling 2s
    inFlightRef.current = true;
    setLoading(true);
    try {
      const r = await api.get("/tracking/live");
      setData(r.data);
      setLastRefresh(new Date());
    } catch (e) {
      // Cooldown de 30s pra não spamear 15 toasts/min se o backend cair
      const nowMs = Date.now();
      if (nowMs - lastErrorToastRef.current > 30000) {
        lastErrorToastRef.current = nowMs;
        toast.error(e?.response?.data?.detail || "Erro ao carregar posições");
      }
      setData((prev) => prev || { entregadores: [], error: true });
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  // Iter 82 (fev/2026): Polling 2s recursivo (setTimeout depois do await) —
  // se uma request demorar 3s, a próxima só arranca depois. Nunca acumula fila.
  // + Pause automático quando a aba fica em background (Page Visibility API)
  //   pra economizar bateria e evitar bombardear o backend com abas ociosas.
  useEffect(() => {
    let cancelled = false;
    const POLL_MS = 2000;

    const tick = async () => {
      if (cancelled) return;
      if (!document.hidden) {
        await load();
      }
      if (!cancelled) setTimeout(tick, POLL_MS);
    };
    tick();

    // Refresh imediato quando o usuário volta pra aba
    const onVisibility = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [currentEmpresaId]);

  // Fallback estável via useMemo — sem short-circuit `|| []` fora do memo (o
  // ESLint rule react-hooks/exhaustive-deps flagga logical expr em deps).
  const entregadores = useMemo(() => {
    const list = data?.entregadores;
    return Array.isArray(list) ? list : [];
  }, [data]);

  // Alerta automático: toast quando entregador ENTRA em no_signal
  useEffect(() => {
    const nowNoSignal = new Set();
    entregadores.forEach((e) => {
      if (computeDriverStatus(e) === "no_signal") nowNoSignal.add(e.entregador_id);
    });
    // Dispara toast pros novos
    nowNoSignal.forEach((eid) => {
      if (!prevNoSignalRef.current.has(eid)) {
        const ent = entregadores.find((e) => e.entregador_id === eid);
        if (ent) {
          toast.error(
            `⚠️ ${ent.nome} está sem sinal há mais de 3 min — GPS pode estar offline`,
            { duration: 8000, id: `nosignal-${eid}` }  // id evita duplicar
          );
        }
      }
    });
    prevNoSignalRef.current = nowNoSignal;
  }, [entregadores]);

  const activeCount = entregadores.length;
  const statusCounts = useMemo(() => {
    const c = { moving: 0, stopped: 0, no_signal: 0, offline: 0 };
    entregadores.forEach((e) => { c[computeDriverStatus(e)]++; });
    return c;
  }, [entregadores]);
  const totalEntregas = entregadores.reduce(
    (acc, e) => acc + (e.entregas_count || 0),
    0
  );

  const selected = useMemo(
    () =>
      entregadores.find((e) => e.entregador_id === selectedEid) || null,
    [entregadores, selectedEid]
  );

  return (
    <div className="space-y-4" data-testid="page-monitoramento-vivo">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
            <Radar className="w-8 h-8 text-violet-600" />
            Monitoramento em Tempo Real
          </h1>
          <p className="text-sm text-muted-foreground">
            Posição GPS ao vivo · painel atualiza a cada 2s · entregadores enviam a cada 2–3s em movimento (10m mínimo)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-xs text-muted-foreground flex items-center gap-1.5"
            data-testid="mon-last-refresh"
            title="Atualiza automaticamente a cada 2 segundos"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            AO VIVO ·{" "}
            {lastRefresh
              ? lastRefresh.toLocaleTimeString("pt-BR")
              : "conectando…"}
          </span>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="btn-monitoramento-refresh"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Atualizar
          </Button>
        </div>
      </div>

      <Tabs defaultValue="live" className="w-full">
        <TabsList className="grid grid-cols-2 w-full max-w-md" data-testid="mon-tabs">
          <TabsTrigger value="live" data-testid="mon-tab-live">
            <Radar className="w-4 h-4 mr-1.5" />
            Ao Vivo
          </TabsTrigger>
          <TabsTrigger value="historico" data-testid="mon-tab-historico">
            <History className="w-4 h-4 mr-1.5" />
            Rastro 24h
          </TabsTrigger>
        </TabsList>

        <TabsContent value="live" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Entregadores em rota
            </div>
            <div
              className="text-3xl font-extrabold text-violet-700"
              data-testid="mon-ativos"
            >
              {activeCount}
            </div>
          </CardContent>
        </Card>
        {[
          { key: "moving",    label: "Em movimento", testid: "mon-moving",    color: "text-emerald-600" },
          { key: "stopped",   label: "Parados",      testid: "mon-stopped",   color: "text-amber-600" },
          { key: "no_signal", label: "Sem sinal",    testid: "mon-nosignal",  color: "text-red-600" },
        ].map((s) => (
          <Card key={s.key}>
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground uppercase">{s.label}</div>
              <div className={`text-3xl font-extrabold ${s.color}`} data-testid={s.testid}>
                {statusCounts[s.key]}
              </div>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase">
              Entregas em rota (total)
            </div>
            <div
              className="text-3xl font-extrabold text-orange-700"
              data-testid="mon-total-entregas"
            >
              {totalEntregas}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4">
        {/* Sidebar — lista de entregadores */}
        <div className="space-y-3 max-h-[72vh] overflow-y-auto pr-1">
          {entregadores.length === 0 && (
            <Card>
              <CardContent
                className="p-6 text-center text-muted-foreground"
                data-testid="mon-empty"
              >
                <Truck className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                Nenhum entregador com entrega em rota agora.
              </CardContent>
            </Card>
          )}
          {entregadores.map((e) => {
            const isSelected = selectedEid === e.entregador_id;
            const status = computeDriverStatus(e);
            const meta = STATUS_META[status];
            const StatusIcon = meta.icon;
            const speedKmh = Number.isFinite(e.position?.speed) ? Math.round((e.position.speed || 0) * 3.6) : null;
            const eta = estimateETA(e.position?.speed);
            const trailColor = colorForDriver(e.entregador_id);
            return (
              <Card
                key={e.entregador_id}
                className={`cursor-pointer transition ${
                  isSelected
                    ? "ring-2 ring-violet-500 shadow-md"
                    : "hover:shadow-md"
                }`}
                onClick={() => setSelectedEid(e.entregador_id)}
                data-testid={`mon-driver-${e.entregador_id}`}
                data-status={status}
              >
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shrink-0 relative"
                      style={{ background: meta.color }}
                    >
                      {initials(e.nome)}
                      {/* Barrinha vertical na cor do trail — vincula com o traço no mapa */}
                      <div className="absolute -left-1 top-1 bottom-1 w-1 rounded-full"
                           style={{ background: trailColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-bold truncate">{e.nome}</div>
                        <Badge
                          className="text-[10px] gap-0.5 border"
                          style={{
                            background: `${meta.color}18`,
                            color: meta.color,
                            borderColor: `${meta.color}55`,
                          }}
                          data-testid={`mon-status-${e.entregador_id}`}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {meta.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                        <span className="inline-flex items-center gap-0.5">
                          <Package className="w-3 h-3" />
                          {e.entregas_count} pendente
                          {e.entregas_count !== 1 ? "s" : ""}
                        </span>
                        {e.position?.minutes_ago !== null &&
                        e.position?.minutes_ago !== undefined ? (
                          <span
                            className="inline-flex items-center gap-0.5"
                            data-testid={`mon-lastping-${e.entregador_id}`}
                          >
                            <Clock className="w-3 h-3" />
                            {e.position.minutes_ago < 1 ? "agora" : `há ${e.position.minutes_ago} min`}
                          </span>
                        ) : (
                          <span className="text-amber-700">
                            aguardando GPS
                          </span>
                        )}
                        {status === "moving" && speedKmh > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold"
                            data-testid={`mon-speed-${e.entregador_id}`}
                          >
                            🏍 {speedKmh} km/h
                          </span>
                        )}
                        {eta && (
                          <span
                            className="inline-flex items-center gap-0.5 text-violet-700 font-semibold"
                            data-testid={`mon-eta-${e.entregador_id}`}
                            title="Estimativa baseada na velocidade atual (raio médio de 2 km)"
                          >
                            <Zap className="w-3 h-3" />
                            chega em ~{eta} min
                          </span>
                        )}
                      </div>
                      {e.telefone && (
                        <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-1">
                          <Phone className="w-3 h-3" />
                          {e.telefone}
                        </div>
                      )}
                      <AcordarGpsButton entregador={e} status={status} />
                    </div>
                  </div>
                  {isSelected && (
                    <div
                      className="mt-3 pt-2 border-t space-y-1.5"
                      data-testid={`mon-entregas-${e.entregador_id}`}
                    >
                      <div className="text-[10px] font-bold text-violet-800 uppercase">
                        Entregas em rota
                      </div>
                      {e.entregas.map((en) => (
                        <div
                          key={en.id}
                          className="text-xs bg-violet-50/60 rounded-md p-2 border border-violet-200"
                          data-testid={`mon-entrega-${en.id}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <b>#{en.numero_pedido || "s/n"}</b>
                            {en.prioridade === "urgente" && (
                              <Badge className="bg-red-600 text-white text-[9px]">
                                🚨
                              </Badge>
                            )}
                            {en.prioridade === "vip" && (
                              <Badge className="bg-purple-600 text-white text-[9px]">
                                ⭐
                              </Badge>
                            )}
                          </div>
                          <div className="truncate">
                            {en.cliente_nome || "Sem nome"}
                          </div>
                          <div className="text-muted-foreground text-[11px]">
                            <MapPin className="w-3 h-3 inline mr-0.5" />
                            {en.bairro_nome || "—"}
                            {en.valor > 0 && (
                              <span className="ml-2 text-orange-700 font-semibold">
                                {fmtBRL(en.valor)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Mapa */}
        <Card>
          <CardContent className="p-2">
            <LiveMap
              entregadores={entregadores}
              focusEid={selectedEid}
              onSelectDriver={setSelectedEid}
            />
          </CardContent>
        </Card>
      </div>

      {selected && !selected.position && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-900"
          data-testid="mon-no-gps-notice"
        >
          <b>{selected.nome}</b> ainda não enviou GPS. O app do entregador só transmite quando o navegador está aberto — em background (celular bloqueado), o rastreamento pausa até voltar ao foreground.
        </div>
      )}
        </TabsContent>

        <TabsContent value="historico" className="mt-4">
          <RastroHistorico />
        </TabsContent>
      </Tabs>
    </div>
  );
}
